'use client'

import React, { useEffect, useState, useRef, useCallback } from 'react'
import Image from 'next/image'
import 'keen-slider/keen-slider.min.css'
import { useKeenSlider } from 'keen-slider/react'
import { OffersOffer } from '@/services/dataTypes'
import { apiRandomOfferBanners } from '@/apis/offers'
import { filterOfferBanners, getBannerDimensions, getBaseImageUrl, getMerchantHref } from '@/constants/hooks'
import OfferOutUrl from '@/components/shared/OfferOutUrl'
import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react'

interface Props {
    companyId: string
    mer_slug: string
    slug_type: string
    domain: string
}

const HorizontalBannerSlider = ({ companyId, slug_type, mer_slug, domain }: Props) => {
    const [offers, setOffers] = useState<OffersOffer[]>([])
    const [loading, setLoading] = useState(false)
    const [hasMore, setHasMore] = useState(true)
    const [pagination, setPagination] = useState<any>(null)
    const [isPlaying, setIsPlaying] = useState(true)
    const [isSliderReady, setIsSliderReady] = useState(false)

    const latestRequest = useRef(0)
    const emptyAttempts = useRef(0)
    const sliderSectionRef = useRef<HTMLElement | null>(null)
    const autoplayRef = useRef<NodeJS.Timeout | null>(null)

    const offersRef = useRef(offers)
    const hasMoreRef = useRef(hasMore)
    const loadingRef = useRef(loading)
    const paginationRef = useRef(pagination)

    useEffect(() => { offersRef.current = offers }, [offers])
    useEffect(() => { hasMoreRef.current = hasMore }, [hasMore])
    useEffect(() => { loadingRef.current = loading }, [loading])
    useEffect(() => { paginationRef.current = pagination }, [pagination])

    const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
        loop: true,
        slides: { perView: 1 },
        mode: 'snap',
        created: () => setIsSliderReady(true),
        slideChanged: (s) => {
            if (
                s.track.details.rel === offersRef.current.length - 1 &&
                hasMoreRef.current &&
                !loadingRef.current &&
                paginationRef.current?.next_page
            ) {
                getOffers(paginationRef.current.next_page)
            }
        },
    })

    const stopAutoplay = () => {
        if (autoplayRef.current) { clearInterval(autoplayRef.current); autoplayRef.current = null }
    }

    const startAutoplay = useCallback(() => {
        if (!slider.current || !isSliderReady || autoplayRef.current) return
        autoplayRef.current = setInterval(() => {
            slider.current?.moveToIdx((slider.current?.track.details.abs || 0) + 1, true, { duration: 3000, easing: t => t })
        }, 5000)
    }, [slider, isSliderReady])

    const toggleAutoplay = () => {
        if (isPlaying) { stopAutoplay(); setIsPlaying(false) }
        else { startAutoplay(); setIsPlaying(true) }
    }

    useEffect(() => {
        if (!slider.current || !isSliderReady) return
        startAutoplay()
        const wrapper = sliderSectionRef.current
        wrapper?.addEventListener('mouseenter', stopAutoplay)
        wrapper?.addEventListener('mouseleave', startAutoplay)
        return () => {
            stopAutoplay()
            wrapper?.removeEventListener('mouseenter', stopAutoplay)
            wrapper?.removeEventListener('mouseleave', startAutoplay)
        }
    }, [slider, isSliderReady, startAutoplay])

    const getOffers = useCallback(async (pageNum: number, reset = false) => {
        setLoading(true)
        const requestId = Date.now()
        latestRequest.current = requestId
        try {
            const bannerOffers = await apiRandomOfferBanners(companyId, pageNum)
            if (latestRequest.current !== requestId) return
            const apiOffers = bannerOffers?.data?.offers || []
            const filtered = filterOfferBanners(apiOffers, 300, 1000, 0, 150)
            const paginationData = bannerOffers?.data?.pagination || {}
            setPagination({
                current_page: paginationData.current_page,
                last_page: paginationData.last_page,
                next_page: paginationData.next_page,
            })
            if (filtered.length === 0 && paginationData.next_page && emptyAttempts.current < 1) {
                emptyAttempts.current += 1
                await getOffers(paginationData.next_page, false)
                return
            } else {
                emptyAttempts.current = 0
            }
            if (reset) setOffers(filtered)
            else if (filtered.length > 0) setOffers(prev => [...prev, ...filtered])
            setHasMore(paginationData.next_page !== null)
        } finally {
            setLoading(false)
        }
    }, [companyId])

    useEffect(() => {
        setOffers([])
        setPagination(null)
        emptyAttempts.current = 0
        getOffers(1, true)
    }, [companyId])

    useEffect(() => { slider.current?.update() }, [offers])

    if (!offers.length && !loading) return null

    return (
        <section ref={sliderSectionRef} className="py-10 px-5" style={{ background: '#FAF7F2' }}>
            <div className="max-w-7xl mx-auto">

                {offers.length > 0 ? (
                    <div className="relative rounded-2xl overflow-hidden"
                        style={{ border: '1px solid rgba(92,122,92,0.15)', boxShadow: '0 4px 24px rgba(45,62,45,0.07)' }}>

                        {/* Slider */}
                        <div ref={sliderRef} className="keen-slider">
                            {offers.map((item, i) => {
                                const dim = getBannerDimensions(item)
                                const { offer_title, offer_detail, banner_image, url } = item?.offer ?? {}
                                const merchantHref = getMerchantHref(item?.merchant, mer_slug, slug_type)

                                return (
                                    <div key={i} className="keen-slider__slide"
                                        style={{ width: '100%', height: 200, display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden', background: '#FFFFFF' }}>
                                        <OfferOutUrl
                                            outUrl={url}
                                            unique_id={item?.offer?.unique_id}
                                            merchantHref={merchantHref}
                                            domain={domain}
                                            customClass="w-full h-full flex items-center justify-center">
                                            <Image
                                                src={getBaseImageUrl(domain, banner_image, '')}
                                                alt={offer_title || offer_detail || 'banner'}
                                                height={dim?.height || 200}
                                                width={dim?.width || 800}
                                                className="object-contain max-h-[200px] w-full"
                                                loading="lazy"
                                            />
                                        </OfferOutUrl>
                                    </div>
                                )
                            })}
                        </div>

                        {/* Prev button */}
                        <button
                            onClick={() => slider.current?.prev()}
                            className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:-translate-y-[calc(50%+2px)]"
                            style={{ background: '#FFFFFF', border: '1px solid rgba(92,122,92,0.2)', color: '#2D3E2D', boxShadow: '0 2px 8px rgba(45,62,45,0.1)' }}
                            aria-label="Previous">
                            <ChevronLeft size={16} />
                        </button>

                        {/* Next button */}
                        <button
                            onClick={() => slider.current?.next()}
                            className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:-translate-y-[calc(50%+2px)]"
                            style={{ background: '#FFFFFF', border: '1px solid rgba(92,122,92,0.2)', color: '#2D3E2D', boxShadow: '0 2px 8px rgba(45,62,45,0.1)' }}
                            aria-label="Next">
                            <ChevronRight size={16} />
                        </button>

                        {/* Bottom bar */}
                        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-4 py-2"
                            style={{ background: 'linear-gradient(to top, rgba(250,247,242,0.95), transparent)' }}>
                            <div />
                        </div>
                    </div>
                ) : (
                    /* Loading skeleton */
                    <div className="h-[200px] rounded-2xl flex items-center justify-center"
                        style={{ background: '#FFFFFF', border: '1px solid rgba(92,122,92,0.12)' }}>
                        <div className="flex flex-col items-center gap-3">
                            <div className="w-7 h-7 rounded-full border-2 border-t-transparent animate-spin"
                                style={{ borderColor: 'rgba(92,122,92,0.3)', borderTopColor: 'transparent' }} />
                            <p className="text-xs font-medium" style={{ color: 'rgba(45,62,45,0.4)' }}>Loading deals...</p>
                        </div>
                    </div>
                )}
            </div>
        </section>
    )
}

export default HorizontalBannerSlider
